import React, { useState, useEffect, useRef } from 'react';
import ClassMemberPicker from './ClassMemberPicker';
import toast from 'react-hot-toast';
import { X, Search, Mail, Phone, Loader, UserX } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getCurrentUser } from '../../profile/services';
import { apiCall } from '../../services/apiUtil';
import { btn } from '../../utils/buttonStyles';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

/**
 * AddInviteMembers - Self-contained component for adding/inviting members
 *
 * Props:
 * - voProvider: SquadDetailsProvider instance (required)
 * - isOpen: Boolean to control modal visibility
 * - onClose: Callback when modal is closed
 */
const AddInviteMembers = ({
  voProvider,
  isOpen,
  onClose,
  invitationId = null,
  reinviteUserId = null,
}) => {
  const { theme } = useTheme();

  // Get current user
  const currentUser = getCurrentUser();
  const currentUserId = currentUser?.id ? String(currentUser.id) : null;

  // Local state - All hooks must be called before any conditional returns
  const [activeMode, setActiveMode] = useState('search'); // 'search', 'email', 'phone'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [addingMemberId, setAddingMemberId] = useState(null);
  const [reinviteUser, setReinviteUser] = useState(null);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  // Escape closes the dialog the same way the X does. The ref holds the current
  // close handler, which is defined further down.
  const closeRef = useRef(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Load existing invitation data when invitationId is provided
  useEffect(() => {
    if (!isOpen || !invitationId) return;

    const loadInvitation = async () => {
      try {
        const inv = await apiCall(`/invitations/${invitationId}`);
        if (inv) {
          setInviteFirstName(inv.firstName || '');
          setInviteLastName(inv.lastName || '');
          if (inv.emailid) {
            setInviteEmail(inv.emailid);
            setActiveMode('email');
          } else if (inv.phoneNumber) {
            // Strip country code for the phone input
            const digits = inv.phoneNumber.replace(/^\+\d+/, '');
            setInvitePhone(digits);
            setActiveMode('phone');
          }
        }
      } catch (err) {
        console.error('Failed to load invitation:', err);
      }
    };

    loadInvitation();
  }, [isOpen, invitationId]);

  // Load user for app-invited member re-invite
  useEffect(() => {
    if (!isOpen || !reinviteUserId) {
      setReinviteUser(null);
      return;
    }
    const loadUser = async () => {
      try {
        const user = await apiCall(`/user/${reinviteUserId}`);
        setReinviteUser(user);
        setActiveMode('search');
      } catch (err) {
        console.error('Failed to load user:', err);
      }
    };
    loadUser();
  }, [isOpen, reinviteUserId]);

  // Disable body scroll when modal is open (mobile-friendly)
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Guard: Return null if voProvider is not available (AFTER all hooks)
  if (!voProvider) {
    return null;
  }

  // Get data from VO (safe to access after guard check)
  const groupId = voProvider.vo.groupId;
  const groupName = voProvider.getGroup()?.name || '';
  const group = voProvider.getGroup();
  // A class squad adds from the people it already has — the program's students
  // and the academy's staff. A reminder to somebody already invited keeps the
  // usual flow.
  const showClassPicker = Boolean(group?.programId) && !invitationId && !reinviteUserId;
  const existingUserIds = (voProvider.getMembers() || []).map(m => m.userId).filter(Boolean);
  const existingMembers = voProvider.getMembers();

  const countryCodes = [
    { code: '+1', country: 'United States', flag: '🇺🇸' },
    { code: '+1', country: 'Canada', flag: '🇨🇦' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  ];

  // Search users
  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 4) {
      return;
    }

    setSearchLoading(true);
    try {
      const result = await apiCall(`/user?search=${encodeURIComponent(searchQuery.trim())}&page=1&limit=10`);
      const users = result.data || [];
      setSearchResults(users);
      setHasMore(users.length === 10);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    }
    setSearchLoading(false);
  };

  // Load more results
  const handleLoadMore = async () => {
    setSearchLoading(true);
    try {
      const nextPage = currentPage + 1;
      const result = await apiCall(`/user?search=${encodeURIComponent(searchQuery.trim())}&page=${nextPage}&limit=10`);
      const users = result.data || [];
      setSearchResults(prev => [...prev, ...users]);
      setHasMore(users.length === 10);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Error loading more users:', error);
      toast.error('Failed to load more users');
    }
    setSearchLoading(false);
  };

  // Add member to group
  const handleAddMember = async (user, successMessage) => {
    setAddingMemberId(user.id);

    try {
      await apiCall(`/groups/${groupId}/members`, {
        method: 'POST',
        body: JSON.stringify({ targetUserId: user.id }),
      });

      toast.success(successMessage || `${user.firstName} ${user.lastName} has been added!`);

      // Refresh members from API and update VO
      const membersResult = await apiCall(`/groups/${groupId}/members`);
      const freshMembers = membersResult.data || membersResult;
      voProvider.setMembers(freshMembers);

      // Remove the added user from search results so user can continue adding others
      setSearchResults(prev => prev.filter(u => u.id !== user.id));
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(error.message || 'Failed to add member. Please try again.');
    }

    setAddingMemberId(null);
  };

  // Invite by email
  const handleInviteEmail = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    if (!inviteFirstName.trim() || !inviteLastName.trim()) {
      toast.error('Please enter first and last name');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      const inviterName = currentUser
        ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'A member'
        : 'A member';

      const result = await apiCall('/invitations/group', {
        method: 'POST',
        body: JSON.stringify({
          email: inviteEmail.trim(),
          firstName: inviteFirstName.trim(),
          lastName: inviteLastName.trim(),
          groupId: groupId,
          groupName: groupName,
          inviterName,
        }),
      });

      if (result.userExists && !isResend) {
        const user = result.existingUser;
        toast.error(`${user.first_name} ${user.last_name} is already registered. Use "Search Users" to add them.`);
        return;
      }

      toast.success(`Reminder sent to ${inviteFirstName} ${inviteLastName}!`);

      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
    } catch (error) {
      console.error('Error sending email invitation:', error);
      toast.error('Failed to send invitation. Please try again.');
    }
  };

  // Invite by phone
  const handleInvitePhone = async () => {
    if (!invitePhone.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    if (!inviteFirstName.trim() || !inviteLastName.trim()) {
      toast.error('Please enter first and last name');
      return;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(invitePhone.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      const fullPhone = countryCode + invitePhone.trim();
      const result = await apiCall('/invitations', {
        method: 'POST',
        body: JSON.stringify({
          phoneNumber: fullPhone,
          firstName: inviteFirstName.trim(),
          lastName: inviteLastName.trim(),
          useBy: `group_${groupId}`,
          status: 'pending',
        }),
      });

      if (result.userExists) {
        const user = result.existingUser;
        toast.error(`${user.first_name} ${user.last_name} is already registered. Use "Search Users" to add them.`);
        return;
      }

      toast.success(`Invitation sent to ${inviteFirstName} ${inviteLastName}!`);
      setInvitePhone('');
      setInviteFirstName('');
      setInviteLastName('');
    } catch (error) {
      console.error('Error sending phone invitation:', error);
      toast.error('Failed to send invitation. Please try again.');
    }
  };

  const handleModeChange = (mode) => {
    setActiveMode(mode);
  };

  const isResend = !!invitationId;

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setReinviteUser(null);
    setInviteEmail('');
    setInvitePhone('');
    setInviteFirstName('');
    setInviteLastName('');
    setActiveMode('search');
    onClose();
  };
  closeRef.current = handleClose;

  // Re-read the squad's members after the class picker adds people.
  const refreshMembers = async () => {
    const membersResult = await apiCall(`/groups/${groupId}/members`);
    voProvider.setMembers(membersResult.data || membersResult);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      // A click on the dimmed backdrop, not on the dialog, closes it.
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`${theme.card} rounded-lg max-w-2xl w-full h-[80vh] max-h-[600px] overflow-hidden flex flex-col my-auto`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text}`}>{isResend ? 'Resend Invitation' : 'Add or Invite Members'}</h3>
          <button
            onClick={handleClose}
            aria-label="Close"
            title="Close"
            className={`p-2 ${theme.cardHover} rounded-full transition-colors`}
          >
            <X className={`w-5 h-5 ${theme.text}`} />
          </button>
        </div>

        {showClassPicker ? (
          <ClassMemberPicker
            groupId={groupId}
            programId={group.programId}
            academyId={group.academyId}
            existingUserIds={existingUserIds}
            onAdded={refreshMembers}
          />
        ) : (
        <>
        {/* Mode Switcher */}
        <div className={`p-4 border-b ${theme.border}`}>
          <div className="flex gap-x-2">
            <button
              onClick={() => handleModeChange('search')}
              className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center ${
                activeMode === 'search'
                  ? 'bg-blue-500 text-white'
                  : `${theme.backgroundSecondary} ${theme.text} hover:${theme.cardHover}`
              }`}
            >
              <Search className="w-4 h-4 mr-1" />
              <span>Search</span>
            </button>
            <button
              onClick={() => handleModeChange('email')}
              className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center ${
                activeMode === 'email'
                  ? 'bg-blue-500 text-white'
                  : `${theme.backgroundSecondary} ${theme.text} hover:${theme.cardHover}`
              }`}
            >
              <Mail className="w-4 h-4 mr-1" />
              <span>Invite</span>
            </button>
            <button
              onClick={() => handleModeChange('phone')}
              className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center ${
                activeMode === 'phone'
                  ? 'bg-blue-500 text-white'
                  : `${theme.backgroundSecondary} ${theme.text} hover:${theme.cardHover}`
              }`}
            >
              <Phone className="w-4 h-4 mr-1" />
              <span>Invite</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 flex-1 overflow-y-auto min-h-0">
          {/* Search Users Mode */}
          {activeMode === 'search' && (
            <div className="h-full flex flex-col">
              <div className="relative mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder="Enter at least 4 characters to search..."
                  className={`w-full pl-4 pr-12 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input} ${theme.border}`}
                />
                <button
                  onClick={handleSearch}
                  disabled={searchLoading || searchQuery.trim().length < 4}
                  className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-md transition-colors ${
                    searchQuery.trim().length >= 4 && !searchLoading
                      ? 'text-blue-500 hover:bg-blue-50 cursor-pointer'
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {searchLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {/* Re-invite user card for app-invited members */}
                {reinviteUser && (
                  <div className={`mb-4 p-3 rounded-lg border-2 border-orange-300 ${theme.backgroundSecondary}`}>
                    <p className={`text-xs font-medium text-orange-600 mb-2`}>Invited member — send a reminder</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-x-3">
                        {reinviteUser.profileImage ? (
                          <img src={resolveImageUrl(reinviteUser.profileImage)} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className={`w-10 h-10 ${theme.gradient} rounded-full flex items-center justify-center`}>
                            <span className="text-lg font-bold text-white">
                              {reinviteUser.firstName?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className={`font-medium ${theme.text}`}>{reinviteUser.firstName} {reinviteUser.lastName}</p>
                          <p className={`text-sm ${theme.textMuted}`}>{reinviteUser.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          setAddingMemberId(reinviteUser.id);
                          try {
                            await apiCall(`/groups/${groupId}/members`, {
                              method: 'POST',
                              body: JSON.stringify({ targetUserId: reinviteUser.id }),
                            });
                            toast.success(`Reminder sent to ${reinviteUser.firstName} ${reinviteUser.lastName} for accepting membership!`);
                          } catch (e) {
                            toast.error('Failed to send reminder. Please try again.');
                          }
                          setAddingMemberId(null);
                        }}
                        disabled={addingMemberId !== null}
                        className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {addingMemberId === reinviteUser.id ? 'Sending...' : 'Re-invite'}
                      </button>
                    </div>
                  </div>
                )}

                {searchLoading && searchResults.length === 0 ? (
                  <div className="flex justify-center py-8">
                    <Loader className="w-6 h-6 animate-spin text-blue-500" />
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    <div className="space-y-2">
                      {searchResults.map((user) => (
                        <div key={user.id} className={`flex items-center justify-between p-3 rounded-lg ${theme.backgroundSecondary}`}>
                          <div className="flex items-center gap-x-3">
                            {user.profileImage ? (
                              <img
                                src={resolveImageUrl(user.profileImage)}
                                alt={`${user.firstName} ${user.lastName}`}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`w-10 h-10 ${theme.gradient} rounded-full flex items-center justify-center`}>
                                <span className="text-lg font-bold text-white">
                                  {user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className={`font-medium ${theme.text}`}>
                                {user.firstName} {user.lastName}
                              </p>
                              <p className={`text-sm ${theme.textMuted}`}>
                                {user.email}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddMember(user)}
                            disabled={addingMemberId !== null}
                            className={`px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {addingMemberId === user.id ? 'Adding...' : 'Add'}
                          </button>
                        </div>
                      ))}
                    </div>

                    {hasMore && (
                      <div className="mt-4 text-center">
                        <button
                          onClick={handleLoadMore}
                          disabled={searchLoading}
                          className="px-6 py-2 text-sm font-medium text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {searchLoading ? 'Loading...' : 'View More'}
                        </button>
                      </div>
                    )}
                  </>
                ) : searchQuery.trim() && searchResults.length === 0 && !searchLoading ? (
                  <div className="text-center py-8">
                    <UserX className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className={`${theme.textMuted}`}>No users found</p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className={`${theme.textMuted}`}>
                      Enter at least 4 characters and search to find users
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Invite by Email Mode */}
          {activeMode === 'email' && (
            <div className="h-full flex flex-col">
              <div className="mb-4">
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  First Name
                </label>
                <input
                  type="text"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  placeholder="Enter first name"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input} ${theme.border}`}
                />
              </div>

              <div className="mb-4">
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  placeholder="Enter last name"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input} ${theme.border}`}
                />
              </div>

              <div className="mb-4">
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Enter email address"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input} ${theme.border}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleInviteEmail();
                    }
                  }}
                />
                <p className={`text-xs ${theme.textMuted} mt-2`}>
                  An invitation will be sent to this email address
                </p>
              </div>

              <button
                onClick={handleInviteEmail}
                className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} w-full`}
              >
                <Mail className="w-4 h-4 inline mr-2" />
                {isResend ? 'Resend Invitation' : 'Send Invitation'}
              </button>

              <div className={`mt-6 p-4 rounded-lg ${theme.backgroundSecondary}`}>
                <h4 className={`font-medium ${theme.text} mb-2`}>How it works</h4>
                <ul className={`text-sm ${theme.textMuted} space-y-1 list-disc list-inside`}>
                  <li>An email invitation will be sent to the provided address</li>
                  <li>The recipient can click the link to join the group</li>
                  <li>They'll need to create an account if they don't have one</li>
                </ul>
              </div>
            </div>
          )}

          {/* Invite by Phone Mode */}
          {activeMode === 'phone' && (
            <div className="h-full flex flex-col">
              <div className="mb-4">
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  First Name
                </label>
                <input
                  type="text"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  placeholder="Enter first name"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input} ${theme.border}`}
                />
              </div>

              <div className="mb-4">
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  placeholder="Enter last name"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input} ${theme.border}`}
                />
              </div>

              <div className="mb-4">
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Country Code
                </label>
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input} ${theme.border}`}
                >
                  {countryCodes.map((country, index) => (
                    <option key={index} value={country.code}>
                      {country.flag} {country.country} ({country.code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Phone Number
                </label>
                <div className="flex gap-x-2">
                  <div className={`px-4 py-2 border rounded-lg ${theme.backgroundSecondary} ${theme.border} flex items-center justify-center min-w-[80px]`}>
                    <span className={`font-medium ${theme.text}`}>{countryCode}</span>
                  </div>
                  <input
                    type="tel"
                    value={invitePhone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setInvitePhone(value);
                    }}
                    placeholder="1234567890"
                    maxLength="10"
                    className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input} ${theme.border}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleInvitePhone();
                      }
                    }}
                  />
                </div>
                <p className={`text-xs ${theme.textMuted} mt-2`}>
                  Enter 10-digit phone number without country code
                </p>
              </div>

              <button
                onClick={handleInvitePhone}
                className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} w-full`}
              >
                <Phone className="w-4 h-4 inline mr-2" />
                Send Invitation
              </button>

              <div className={`mt-6 p-4 rounded-lg ${theme.backgroundSecondary}`}>
                <h4 className={`font-medium ${theme.text} mb-2`}>How it works</h4>
                <ul className={`text-sm ${theme.textMuted} space-y-1 list-disc list-inside`}>
                  <li>An SMS invitation will be sent to the provided number</li>
                  <li>The recipient can click the link to join the group</li>
                  <li>They'll need to create an account if they don't have one</li>
                </ul>
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default AddInviteMembers;
